/**
 * A világ futásideje: kollízió, sugárkövetés, ajtók, liftek, kapcsolók.
 * Ez a `WorldApi` szerződés egyetlen implementációja.
 *
 * Kollíziós modell (Doom-szerű): minden test egy kör az XZ-síkon, a falak
 * szakaszok. A feloldás pozíció-alapú, iteratív kitolás — ez adja ingyen a
 * falak menti csúszást, és nem „ragad be" a sarkokban.
 *
 * Gazda: C
 */

import * as THREE from 'three'
import type {
  Actor,
  MoveResult,
  WorldApi,
  WorldHit,
  UseResult,
  KeyColor,
  GameEventMap,
} from '../core/contracts'
import type { EventBus } from '../core/events'
import type { LevelDef } from './level'
import { buildLevel, cellKey, type BuiltLevel, type WallSeg, type RoomRuntime } from './build'
import { closestPointOnSegment, pointInPolygon, raySegment, clamp, type Vec2 } from '../core/math'

/** Hányszor iterálunk a behatolás-feloldáson. 3 elég a sarkokhoz. */
const RESOLVE_ITERATIONS = 4
/** Kis rés, hogy a test ne feküdjön pontosan a falon (numerikus stabilitás). */
const SKIN = 0.001

const _cp: Vec2 = { x: 0, z: 0 }
const _v3a = new THREE.Vector3()

export class World implements WorldApi {
  readonly built: BuiltLevel
  readonly level: LevelDef
  private readonly events: EventBus<GameEventMap>
  private readonly roomIndex = new Map<number, RoomRuntime>()
  /**
   * Lekérdezés-bélyeg a szegmens-gyűjtéshez. A korábbi `out.includes(i)`
   * O(n²) volt: minden rácscellából újra végignézte az addigi találatokat.
   * Egy monoton növekvő bélyeggel a duplikátumszűrés elemenként O(1).
   * (X talált rá — `09-X-revizio.md` F6.)
   */
  private segStamp: Int32Array
  private queryId = 0
  /** Az aktorok listája — az aktor-aktor ütközéshez. A store állítja be. */
  actorsRef: readonly Actor[] = []

  constructor(def: LevelDef, events: EventBus<GameEventMap>) {
    this.built = buildLevel(def)
    this.level = def
    this.events = events
    this.segStamp = new Int32Array(this.built.segments.length)
    for (const r of this.built.rooms) this.roomIndex.set(r.id, r)
  }

  get group(): THREE.Group {
    return this.built.group
  }

  dispose(): void {
    this.built.dispose()
  }

  // -------------------------------------------------------------------------
  // Szoba- és magasság-lekérdezések
  // -------------------------------------------------------------------------

  roomAt(x: number, z: number, hint?: number): number {
    // A tipp (előző szoba) az esetek túlnyomó részében talál — így a
    // lekérdezés O(1)-nek érződik, nem O(szobaszám)-nak.
    if (hint !== undefined) {
      const rr = this.roomIndex.get(hint)
      if (rr && this.insideRoom(rr, x, z)) return hint
    }
    for (const rr of this.built.rooms) {
      if (this.insideRoom(rr, x, z)) return rr.id
    }
    return hint ?? (this.built.rooms[0]?.id ?? 0)
  }

  private insideRoom(rr: RoomRuntime, x: number, z: number): boolean {
    const bb = rr.aabb
    if (x < bb.minX || x > bb.maxX || z < bb.minZ || z > bb.maxZ) return false
    return pointInPolygon(x, z, rr.def.poly)
  }

  floorAt(x: number, z: number, hint?: number): number {
    const id = this.roomAt(x, z, hint)
    const rr = this.roomIndex.get(id)
    let y = rr ? rr.floorY : 0

    // Rálépehető propok (láda, emelvény) megemelik a padlót.
    for (const p of this.built.props) {
      if (!p.solid) continue
      if (p.bottom > y + 0.25) continue
      if (p.top <= y) continue
      if (!this.pointInProp(p.id, x, z)) continue
      y = Math.max(y, p.top)
    }
    return y
  }

  ceilAt(x: number, z: number, hint?: number): number {
    const id = this.roomAt(x, z, hint)
    const rr = this.roomIndex.get(id)
    return rr ? rr.ceilY : 100
  }

  hazardAt(x: number, z: number, hint?: number): number {
    const rr = this.roomIndex.get(this.roomAt(x, z, hint))
    return rr?.def.hazard ?? 0
  }

  roomRuntime(id: number): RoomRuntime | undefined {
    return this.roomIndex.get(id)
  }

  private pointInProp(propId: number, x: number, z: number): boolean {
    const p = this.built.props.find((q) => q.id === propId)
    if (!p) return false
    if (p.kind === 'cylinder') {
      return (x - p.pos.x) ** 2 + (z - p.pos.z) ** 2 <= p.size.x * p.size.x
    }
    const yaw = p.yaw ?? 0
    const c = Math.cos(-yaw)
    const s = Math.sin(-yaw)
    const lx = (x - p.pos.x) * c - (z - p.pos.z) * s
    const lz = (x - p.pos.x) * s + (z - p.pos.z) * c
    return Math.abs(lx) <= p.size.x && Math.abs(lz) <= p.size.z
  }

  // -------------------------------------------------------------------------
  // Szegmens-lekérdezés a térbeli rácsból
  // -------------------------------------------------------------------------

  /** A megadott AABB-t érintő szegmensek indexei, `out`-ba gyűjtve. */
  private gatherSegments(minX: number, minZ: number, maxX: number, maxZ: number, out: number[]): number[] {
    out.length = 0
    const cs = this.built.cellSize
    const x0 = Math.floor(minX / cs)
    const x1 = Math.floor(maxX / cs)
    const z0 = Math.floor(minZ / cs)
    const z1 = Math.floor(maxZ / cs)
    const stamp = ++this.queryId
    for (let cz = z0; cz <= z1; cz++) {
      for (let cx = x0; cx <= x1; cx++) {
        const list = this.built.grid.get(cellKey(cx, cz))
        if (!list) continue
        for (const i of list) {
          if (this.segStamp[i] === stamp) continue
          this.segStamp[i] = stamp
          out.push(i)
        }
      }
    }
    return out
  }

  /**
   * Blokkolja-e ez a szakasz a megadott magasságban lévő testet?
   * Ez az egyetlen hely, ahol eldől, mi jár át egy nyíláson.
   */
  private blocks(s: WallSeg, feetY: number, height: number, stepHeight: number): boolean {
    if (s.portal >= 0) {
      const pr = this.built.portals[s.portal]
      // A nyílás alsó pereme a TÚLOLDALI szoba AKTUÁLIS padlója is lehet.
      //
      // Ez a liftek miatt kell. A portál `bottom` mezője építéskor a lift
      // LEGALSÓ állásából számolódik, hogy sose legyen rés a geometriában.
      // Ha viszont a lift felment, a szomszéd szoba padlója magasabban van —
      // és ha csak a statikus `bottom`-ot néznénk, a játékos besétálhatna a
      // felemelt lift ALÁ, ahol a gravitációs feloldás azonnal felrántaná a
      // lift tetejére. Az aktuális padlót figyelembe véve viszont a nyílás
      // egyszerűen túl magas: nem lehet bemenni, ahogy egy valódi liftnél sem.
      const otherId = pr.rooms[0] === s.room ? pr.rooms[1] : pr.rooms[0]
      const otherFloor = this.roomIndex.get(otherId)?.floorY ?? pr.bottom
      const bottom = Math.max(pr.bottom, otherFloor)
      // Csukott ajtó: a felső perem lecsúszik a küszöbre.
      const top = pr.door ? pr.bottom + pr.door.travel * pr.door.open : pr.top
      if (bottom > feetY + stepHeight + 1e-3) return true
      if (top < feetY + height - 1e-3) return true
      return false
    }
    if (s.prop >= 0) {
      // Alacsony prop: fel lehet rá lépni, nem akadály.
      if (s.top <= feetY + stepHeight + 1e-3) return false
      if (s.bottom >= feetY + height - 1e-3) return false
      return true
    }
    return true
  }

  // -------------------------------------------------------------------------
  // Mozgatás
  // -------------------------------------------------------------------------

  private segBuf: number[] = []

  moveActor(actor: Actor, dx: number, dz: number, stepHeight: number): MoveResult {
    // A feloldás a CÉLPOZÍCIÓ átfedését nézi. Ha egyetlen lépés hosszabb a
    // testnél, a cél már a fal túloldalán lehet átfedés nélkül — vagyis
    // átcsúszunk rajta. Ezért a hosszú elmozdulást a sugárnál rövidebb
    // alépésekre bontjuk. (X talált rá — `09-X-revizio.md` F3.)
    const total = Math.hypot(dx, dz)
    const maxStep = actor.radius * 0.75
    if (total > maxStep) {
      const steps = Math.min(16, Math.ceil(total / maxStep))
      let last: MoveResult = {
        moved: false, hitWall: false, wallNormal: null, steppedUp: 0, blockedBy: null,
      }
      for (let i = 0; i < steps; i++) {
        const r2 = this.moveActorStep(actor, dx / steps, dz / steps, stepHeight)
        last.moved = last.moved || r2.moved
        if (r2.hitWall) {
          last.hitWall = true
          last.wallNormal = r2.wallNormal
        }
        if (r2.blockedBy) last.blockedBy = r2.blockedBy
        // Ha nekiment egy falnak, a maradék lépések már csúsztatva mennek —
        // ezt a következő iteráció feloldása magától elvégzi.
      }
      return last
    }
    return this.moveActorStep(actor, dx, dz, stepHeight)
  }

  private moveActorStep(actor: Actor, dx: number, dz: number, stepHeight: number): MoveResult {
    const r = actor.radius
    const startX = actor.pos.x
    const startZ = actor.pos.z
    let x = startX + dx
    let z = startZ + dz

    const result: MoveResult = {
      moved: false,
      hitWall: false,
      wallNormal: null,
      steppedUp: 0,
      blockedBy: null,
    }

    // Sávos lekérdezés: a teljes elmozdulást lefedő AABB + sugár.
    const pad = r + 0.5
    const segs = this.gatherSegments(
      Math.min(startX, x) - pad,
      Math.min(startZ, z) - pad,
      Math.max(startX, x) + pad,
      Math.max(startZ, z) + pad,
      this.segBuf,
    )

    const feetY = actor.pos.y

    for (let iter = 0; iter < RESOLVE_ITERATIONS; iter++) {
      let worstDepth = 0
      let nx = 0
      let nz = 0
      let any = false

      for (const si of segs) {
        const s = this.built.segments[si]
        if (!this.blocks(s, feetY, actor.height, stepHeight)) continue

        closestPointOnSegment(x, z, s.ax, s.az, s.bx, s.bz, _cp)
        let ox = x - _cp.x
        let oz = z - _cp.z
        let d = Math.hypot(ox, oz)
        if (d >= r) continue

        if (d < 1e-6) {
          // Pontosan a falon állunk: a szakasz normálisát használjuk.
          ox = s.nx
          oz = s.nz
          d = 1e-6
        } else {
          ox /= d
          oz /= d
        }
        const depth = r - d + SKIN
        x += ox * depth
        z += oz * depth
        any = true
        if (depth > worstDepth) {
          worstDepth = depth
          nx = ox
          nz = oz
        }
      }

      if (any) {
        result.hitWall = true
        result.wallNormal = { x: nx, z: nz }
      } else {
        break
      }
    }

    // Aktor-aktor ütközés: csak a mozgó testet toljuk ki, a másikat nem —
    // így nem keletkezik kölcsönös lökdösődés-oszcilláció.
    if (actor.solid) {
      for (const other of this.actorsRef) {
        if (other === actor || !other.alive || !other.solid) continue
        // Függőlegesen elkerülik egymást? (pl. emelvényen álló ellenfél)
        if (other.pos.y + other.height < feetY + 0.1) continue
        if (feetY + actor.height < other.pos.y + 0.1) continue

        const sum = r + other.radius
        let ox = x - other.pos.x
        let oz = z - other.pos.z
        const d2 = ox * ox + oz * oz
        if (d2 >= sum * sum || d2 < 1e-9) continue
        const d = Math.sqrt(d2)
        ox /= d
        oz /= d
        const push = sum - d
        x += ox * push
        z += oz * push
        result.blockedBy = other
      }
    }

    // Ha a feloldás után is falban lennénk, inkább maradjunk a kiinduló
    // pozíción, mint hogy átcsússzunk a geometrián.
    if (this.deepInside(x, z, r, feetY, actor.height, stepHeight, segs)) {
      x = startX
      z = startZ
      result.hitWall = true
    }

    result.moved = Math.abs(x - startX) > 1e-6 || Math.abs(z - startZ) > 1e-6
    actor.pos.x = x
    actor.pos.z = z
    actor.roomId = this.roomAt(x, z, actor.roomId)
    return result
  }

  private deepInside(
    x: number,
    z: number,
    r: number,
    feetY: number,
    height: number,
    stepHeight: number,
    segs: number[],
  ): boolean {
    for (const si of segs) {
      const s = this.built.segments[si]
      if (!this.blocks(s, feetY, height, stepHeight)) continue
      closestPointOnSegment(x, z, s.ax, s.az, s.bx, s.bz, _cp)
      const d = Math.hypot(x - _cp.x, z - _cp.z)
      if (d < r - 0.02) return true
    }
    return false
  }

  // -------------------------------------------------------------------------
  // Sugárkövetés
  // -------------------------------------------------------------------------

  private rayBuf: number[] = []

  raycast(origin: THREE.Vector3, dir: THREE.Vector3, maxDist: number): WorldHit | null {
    // 1) Falak: a sugár által érintett cellák szegmensei.
    const endX = origin.x + dir.x * maxDist
    const endZ = origin.z + dir.z * maxDist
    const segs = this.gatherSegments(
      Math.min(origin.x, endX) - 0.1,
      Math.min(origin.z, endZ) - 0.1,
      Math.max(origin.x, endX) + 0.1,
      Math.max(origin.z, endZ) + 0.1,
      this.rayBuf,
    )

    // A vízszintes komponens hossza — a szakasz-metszés 2D-ben történik,
    // ezért a 2D-s távolságot vissza kell váltani 3D-re.
    const hLen = Math.hypot(dir.x, dir.z)
    let best = maxDist
    let bestSeg: WallSeg | null = null

    if (hLen > 1e-6) {
      const ndx = dir.x / hLen
      const ndz = dir.z / hLen
      for (const si of segs) {
        const s = this.built.segments[si]
        const t2 = raySegment(origin.x, origin.z, ndx, ndz, s.ax, s.az, s.bx, s.bz)
        if (t2 < 0) continue
        // 2D távolság → 3D távolság
        const t3 = t2 / hLen
        if (t3 >= best) continue
        const y = origin.y + dir.y * t3
        // Portálon és alacsony propon átmegy a lövés, ha a nyílásban halad.
        if (s.portal >= 0) {
          const pr = this.built.portals[s.portal]
          const top = pr.door ? pr.bottom + pr.door.travel * pr.door.open : pr.top
          // Ugyanaz a megfontolás, mint a `blocks()`-ban: a felemelt lift
          // teste zárja a nyílást, tehát a lövés se menjen át alatta.
          const otherId = pr.rooms[0] === s.room ? pr.rooms[1] : pr.rooms[0]
          const bottom = Math.max(pr.bottom, this.roomIndex.get(otherId)?.floorY ?? pr.bottom)
          if (y > bottom && y < top) continue
        } else if (s.prop >= 0) {
          if (y < s.bottom || y > s.top) continue
        }
        best = t3
        bestSeg = s
      }
    }

    // 2) Padló és mennyezet: lépegetve mintavételezünk, majd a keresztezésre
    //    felezéssel finomítunk. A szobánként változó magasság miatt nincs
    //    egyetlen zárt alakú megoldás.
    const floorHit = this.traceVertical(origin, dir, Math.min(best, maxDist))
    if (floorHit && floorHit.distance < best) {
      return floorHit
    }

    if (!bestSeg) return null

    const point = new THREE.Vector3(
      origin.x + dir.x * best,
      origin.y + dir.y * best,
      origin.z + dir.z * best,
    )
    // A normális a sugárral szembe nézzen.
    let nx = bestSeg.nx
    let nz = bestSeg.nz
    if (nx * dir.x + nz * dir.z > 0) {
      nx = -nx
      nz = -nz
    }
    return {
      distance: best,
      point,
      normal: new THREE.Vector3(nx, 0, nz),
      surface: bestSeg.portal >= 0 ? 'door' : bestSeg.prop >= 0 ? 'prop' : 'wall',
      impact: bestSeg.impact,
    }
  }

  /** Padló-/mennyezet-metszés lépegetéssel + felezéssel. */
  private traceVertical(origin: THREE.Vector3, dir: THREE.Vector3, maxDist: number): WorldHit | null {
    if (Math.abs(dir.y) < 1e-6) return null
    const step = 0.35
    let prevT = 0
    let prevInside = true
    let hint = this.roomAt(origin.x, origin.z)

    for (let t = step; t <= maxDist + step; t += step) {
      const tt = Math.min(t, maxDist)
      const x = origin.x + dir.x * tt
      const y = origin.y + dir.y * tt
      const z = origin.z + dir.z * tt
      hint = this.roomAt(x, z, hint)
      const f = this.floorAt(x, z, hint)
      const c = this.ceilAt(x, z, hint)
      const inside = y >= f && y <= c

      if (!inside && prevInside) {
        // Felezés a pontos keresztezésig
        let lo = prevT
        let hi = tt
        for (let k = 0; k < 12; k++) {
          const mid = (lo + hi) / 2
          const mx = origin.x + dir.x * mid
          const my = origin.y + dir.y * mid
          const mz = origin.z + dir.z * mid
          const h2 = this.roomAt(mx, mz, hint)
          if (my >= this.floorAt(mx, mz, h2) && my <= this.ceilAt(mx, mz, h2)) lo = mid
          else hi = mid
        }
        const px = origin.x + dir.x * lo
        const py = origin.y + dir.y * lo
        const pz = origin.z + dir.z * lo
        const room = this.roomIndex.get(this.roomAt(px, pz, hint))
        const isFloor = dir.y < 0
        // Az égbolt nem állítja meg a lövést.
        if (!isFloor && room?.def.sky) return null
        return {
          distance: lo,
          point: new THREE.Vector3(px, py, pz),
          normal: new THREE.Vector3(0, isFloor ? 1 : -1, 0),
          surface: isFloor ? 'floor' : 'ceiling',
          impact: isFloor ? impactForTex(room?.def.tex.floor) : impactForTex(room?.def.tex.ceil),
        }
      }
      prevInside = inside
      prevT = tt
      if (tt >= maxDist) break
    }
    return null
  }

  lineOfSight(from: THREE.Vector3, to: THREE.Vector3): boolean {
    _v3a.subVectors(to, from)
    const dist = _v3a.length()
    if (dist < 1e-4) return true
    _v3a.multiplyScalar(1 / dist)
    const hit = this.raycast(from, _v3a, dist - 0.05)
    return hit === null
  }

  canWalk(from: THREE.Vector3, to: THREE.Vector3, radius: number, stepHeight: number): boolean {
    const dx = to.x - from.x
    const dz = to.z - from.z
    const dist = Math.hypot(dx, dz)
    if (dist < 1e-4) return true
    const steps = Math.max(2, Math.ceil(dist / Math.max(0.35, radius)))
    let prevFloor = this.floorAt(from.x, from.z)
    let hint = this.roomAt(from.x, from.z)

    for (let i = 1; i <= steps; i++) {
      const t = i / steps
      const x = from.x + dx * t
      const z = from.z + dz * t
      hint = this.roomAt(x, z, hint)
      const f = this.floorAt(x, z, hint)
      // Túl nagy lépcső fel vagy le → nem járható.
      if (f - prevFloor > stepHeight + 1e-3) return false
      if (prevFloor - f > 2.5) return false
      // Elfér-e a test?
      if (this.ceilAt(x, z, hint) - f < 1.9) return false

      const segs = this.gatherSegments(x - radius, z - radius, x + radius, z + radius, this.segBuf)
      for (const si of segs) {
        const s = this.built.segments[si]
        if (!this.blocks(s, f, 1.8, stepHeight)) continue
        closestPointOnSegment(x, z, s.ax, s.az, s.bx, s.bz, _cp)
        if (Math.hypot(x - _cp.x, z - _cp.z) < radius * 0.92) return false
      }
      prevFloor = f
    }
    return true
  }

  // -------------------------------------------------------------------------
  // Ajtók, liftek, kapcsolók
  // -------------------------------------------------------------------------

  tryUse(origin: THREE.Vector3, dir: THREE.Vector3, maxDist: number, keys: ReadonlySet<KeyColor>): UseResult {
    const hLen = Math.hypot(dir.x, dir.z)
    if (hLen < 1e-6) return 'none'
    const ndx = dir.x / hLen
    const ndz = dir.z / hLen

    const segs = this.gatherSegments(
      origin.x - maxDist,
      origin.z - maxDist,
      origin.x + maxDist,
      origin.z + maxDist,
      this.rayBuf,
    )

    let bestT = maxDist
    let bestPortal = -1
    for (const si of segs) {
      const s = this.built.segments[si]
      if (s.portal < 0) continue
      const pr = this.built.portals[s.portal]
      if (!pr.door) continue
      const t = raySegment(origin.x, origin.z, ndx, ndz, s.ax, s.az, s.bx, s.bz)
      if (t < 0 || t >= bestT) continue
      bestT = t
      bestPortal = s.portal
    }

    if (bestPortal < 0) return 'none'
    const door = this.built.portals[bestPortal].door!
    if (door.def.remoteOnly) return 'locked'
    if (door.def.key && !keys.has(door.def.key)) return 'locked'
    return this.openDoor(door) ? 'opened' : 'none'
  }

  private openDoor(door: DoorRuntimeAlias): boolean {
    if (door.state === 'opening' || door.state === 'open') {
      // Nyitott ajtó kézi zárása — a Doom is engedte.
      if (door.state === 'open' && door.def.waitTime === 0) {
        door.state = 'closing'
        return true
      }
      return false
    }
    door.state = 'opening'
    this.events.emit('doorOpened', { portalId: door.portalId })
    return true
  }

  triggerTag(tag: string): boolean {
    let any = false
    for (const d of this.built.doors) {
      if (d.def.tag === tag) {
        if (d.state === 'closed' || d.state === 'closing') {
          d.state = 'opening'
          this.events.emit('doorOpened', { portalId: d.portalId })
        }
        any = true
      }
    }
    for (const l of this.built.lifts) {
      if (l.def.tag === tag) {
        this.startLift(l)
        any = true
      }
    }
    return any
  }

  private startLift(l: (typeof this.built.lifts)[number]): void {
    if (l.state === 'moving') return
    const low = Math.min(l.def.lowY, l.def.highY)
    const high = Math.max(l.def.lowY, l.def.highY)
    l.target = Math.abs(l.y - low) < 0.01 ? high : low
    l.state = 'moving'
  }

  /** Fix lépésközzel hívandó: ajtók és liftek animálása. */
  update(dt: number, actors: readonly Actor[]): void {
    for (const d of this.built.doors) {
      // Ne csukódjon rá senkire.
      d.blocked = this.doorBlocked(d, actors)

      switch (d.state) {
        case 'opening':
          d.open = Math.min(1, d.open + (d.def.speed * dt) / Math.max(0.001, d.travel))
          if (d.open >= 1) {
            d.state = 'open'
            d.waitLeft = d.def.waitTime
          }
          break
        case 'open':
          if (d.def.waitTime > 0) {
            d.waitLeft -= dt
            if (d.waitLeft <= 0 && !d.blocked) d.state = 'closing'
          }
          break
        case 'closing':
          if (d.blocked) {
            d.state = 'opening'
            break
          }
          d.open = Math.max(0, d.open - (d.def.speed * dt) / Math.max(0.001, d.travel))
          if (d.open <= 0) d.state = 'closed'
          break
        case 'closed':
          break
      }
      d.mesh.position.y = d.baseY + d.travel / 2 + d.travel * d.open
    }

    for (const l of this.built.lifts) {
      const rr = this.roomIndex.get(l.roomId)
      if (!rr) continue

      if (l.def.autoTrigger && l.state === 'idle') {
        // Ha valaki ráállt, indul.
        for (const a of actors) {
          if (!a.alive || a.kind !== 'player') continue
          if (a.roomId === l.roomId && Math.abs(a.pos.y - l.y) < 0.4) {
            this.startLift(l)
            break
          }
        }
      }

      if (l.state === 'moving') {
        const dir = Math.sign(l.target - l.y)
        const before = l.y
        l.y += dir * l.def.speed * dt
        if ((dir > 0 && l.y >= l.target) || (dir < 0 && l.y <= l.target) || dir === 0) {
          l.y = l.target
          l.state = l.def.waitTime > 0 ? 'waiting' : 'idle'
          l.waitLeft = l.def.waitTime
        }
        // A rajta állókat VISSZÜK. Enélkül a süllyedő lift kicsúszik a
        // játékos alól (aki utána zuhan, akár esési sebzéssel), az emelkedő
        // pedig egy tick késéssel rángatja fel.
        // (X talált rá — `09-X-revizio.md` F2.)
        const dy = l.y - before
        if (dy !== 0) this.carryRiders(l.roomId, before, dy, actors)
      } else if (l.state === 'waiting') {
        l.waitLeft -= dt
        if (l.waitLeft <= 0) {
          const low = Math.min(l.def.lowY, l.def.highY)
          const high = Math.max(l.def.lowY, l.def.highY)
          l.target = Math.abs(l.y - low) < 0.01 ? high : low
          l.state = 'moving'
        }
      }

      rr.floorY = l.y
      l.mesh.position.y = l.y
    }
  }

  /**
   * A lift padlóján álló aktorok együttmozgatása.
   *
   * „Rajta áll", ha a szoba fölött van és a lába a régi padlószinthez elég
   * közel (a fejük fölött átrepülő lövedék nem számít). Emelkedésnél a
   * mennyezet is korlátoz: aki nem fér el, azt a plafon alá szorítjuk,
   * nem toljuk bele.
   */
  private carryRiders(roomId: number, oldFloorY: number, dy: number, actors: readonly Actor[]): void {
    for (const a of actors) {
      if (!a.alive || a.kind === 'projectile') continue
      if (a.roomId !== roomId) continue
      if (Math.abs(a.pos.y - oldFloorY) > 0.35) continue

      a.pos.y += dy
      if (dy > 0) {
        const ceil = this.ceilAt(a.pos.x, a.pos.z, a.roomId)
        if (a.pos.y + a.height > ceil) a.pos.y = Math.max(oldFloorY, ceil - a.height)
        // Emelkedés közben ne kapjon esési sebzést, és ne „ugorjon" a kamera.
        if (a.vel.y < 0) a.vel.y = 0
      }
      a.onGround = true
    }
  }

  private doorBlocked(d: DoorRuntimeAlias, actors: readonly Actor[]): boolean {
    const pr = this.built.portals[this.portalIndexById(d.portalId)]
    if (!pr) return false
    const midX = (pr.def.p0.x + pr.def.p1.x) / 2
    const midZ = (pr.def.p0.z + pr.def.p1.z) / 2
    const halfLen = Math.hypot(pr.def.p1.x - pr.def.p0.x, pr.def.p1.z - pr.def.p0.z) / 2
    for (const a of actors) {
      if (!a.alive || !a.solid) continue
      const d2 = (a.pos.x - midX) ** 2 + (a.pos.z - midZ) ** 2
      if (d2 < (halfLen + a.radius) ** 2) return true
    }
    return false
  }

  private portalIndexById(id: number): number {
    for (let i = 0; i < this.built.portals.length; i++) {
      if (this.built.portals[i].id === id) return i
    }
    return -1
  }

  /** Titkos szobák felfedezése — a játékos pozíciója alapján. */
  checkSecrets(player: Actor): void {
    const rr = this.roomIndex.get(player.roomId)
    if (rr && rr.def.secret && !rr.discovered) {
      rr.discovered = true
      this.events.emit('secretFound', { roomId: rr.id })
    }
  }

  get secretsTotal(): number {
    return this.built.rooms.filter((r) => r.def.secret).length
  }

  /** Rálépéssel induló liftek indítása a játékos szobájában. */
  get liftRooms(): readonly number[] {
    return this.built.lifts.map((l) => l.roomId)
  }

  clampToWorld(v: THREE.Vector3): void {
    const b = this.built.bounds
    v.x = clamp(v.x, b.minX - 1, b.maxX + 1)
    v.z = clamp(v.z, b.minZ - 1, b.maxZ + 1)
  }
}

type DoorRuntimeAlias = BuiltLevel['doors'][number]

function impactForTex(tex: string | undefined): WorldHit['impact'] {
  if (!tex) return 'concrete'
  if (tex === 'grate' || tex === 'metalPlate' || tex === 'metalRibbed' || tex === 'techPanel') return 'metal'
  if (tex === 'nukage' || tex === 'lightPanel') return 'energy'
  if (tex === 'flesh') return 'flesh'
  return 'concrete'
}
